import { useEffect, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { useLocation } from 'preact-iso';
import { Wordmark } from '../components/Wordmark';
import { Button } from '../components/Button';
import {
  getCurrentUser, restoreSession, logout, type SessionUser,
} from '../lib/auth';

type Props = {
  requireAdmin?: boolean;
  children: (user: SessionUser) => ComponentChildren;
};

// RequireSession gates a page behind a logged-in session. It reuses the single
// magic-link login: when there's no session it stashes the intended path and
// sends the visitor to /auth, which redirects back here after sign-in. There is
// no separate login screen per area — one login, many destinations.
export function RequireSession({ requireAdmin = false, children }: Props) {
  const [phase, setPhase] = useState<'loading' | 'ready' | 'forbidden'>('loading');
  const [user, setUser] = useState<SessionUser | null>(getCurrentUser());
  const { route } = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let u = getCurrentUser();
      if (!u) u = await restoreSession();
      if (cancelled) return;
      if (!u) {
        try {
          localStorage.setItem('sm_next', location.pathname + location.search);
        } catch { /* private mode — fall back to default landing */ }
        route('/auth', true);
        return;
      }
      setUser(u);
      setPhase(requireAdmin && u.role !== 'admin' ? 'forbidden' : 'ready');
    })();
    return () => { cancelled = true; };
  }, []);

  if (phase === 'loading') {
    return (
      <Splash>
        <p style={{ color: 'var(--ink-2)' }}>Loading…</p>
      </Splash>
    );
  }

  if (phase === 'forbidden') {
    return (
      <Splash>
        <Wordmark size={28} />
        <h1 class="wordmark" style={{ margin: '16px 0 0', fontSize: 30, color: 'var(--wine)' }}>
          Not authorized
        </h1>
        <p style={{ marginTop: 8, color: 'var(--ink-2)' }}>
          You're signed in as <strong>{user?.email}</strong>, but this account
          isn't an admin.
        </p>
        <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'center' }}>
          <Button href="/app" variant="ember" size="sm">Go to your diary</Button>
          <Button variant="secondary" size="sm" onClick={() => logout().then(() => route('/auth'))}>
            Sign out
          </Button>
        </div>
      </Splash>
    );
  }

  return <>{children(user!)}</>;
}

function Splash({ children }: { children: ComponentChildren }) {
  return (
    <main
      class="page-in"
      style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center',
      }}
    >
      {children}
    </main>
  );
}
